import _ from 'lodash';
import { ChangeEvent, FC, Fragment, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { TemplateFlexGridContainer } from '../../../components/Grid';
import { Heading } from '../../../components/Heading';
import { InfoText } from '../../../components/InfoText';
import { Input } from '../../../components/Input';
import { useCountryMarketState } from '../../../controllers/CountryMarketController/CountryMarketState';
import { Price, Prices, StepProps } from '../../../types';
import { useBriefingState } from '../BriefingState';
import { filterByCountryMarketSpecificDeps } from '../stepHelpers';
import { stepConfig } from './config';

export const StepPrice: FC<StepProps> = () => {
  const [{ prices }, { setPrices }] = useBriefingState();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [{ countryMarketConfig }] = useCountryMarketState();

  const fieldConfigs = filterByCountryMarketSpecificDeps(
    stepConfig,
    countryMarketConfig?.eLabCountrySlug,
    countryMarketConfig?.eLabMarketSlug,
  );

  const UnitDisplay = useMemo(() =>
    <div className='text-8xl font-bold mt-3 text-gray-700 md:mt-0 md:ml-6 leading-none'>
      {countryMarketConfig?.currency} /{' '}
      {countryMarketConfig?.weightUnit}
    </div>,
  [countryMarketConfig]);

  return (
    <TemplateFlexGridContainer.BriefingStep>
      <Heading
        level="h1"
        margin={0}
        text={t(`topics:price:heading:general`)}
        optionalField={false}
      />
      <TemplateFlexGridContainer.BriefingStepBody>
        <TemplateFlexGridContainer.BriefingStepSection>
          <div className="flex flex-col">
            {fieldConfigs.map(({ key }) => {
              const onChange = (
                type: keyof Price,
                { target: { value } }: ChangeEvent<HTMLInputElement>,
              ) => {
                if (value && isNaN(parseFloat(value))) return;

                // Construct a new object with updated price values and
                // remove any falsey values and/or keys that hold empty objects
                // for the rendering on page Step Review
                const newPrices: Prices = _({
                  ...prices,
                  [key]: _.omitBy(
                    {
                      ...(prices[key] || {}),
                      [type]: parseFloat(value) || null,
                    },
                    _.isNil,
                  ),
                })
                  .omitBy(_.isNil)
                  .omitBy(_.isEmpty)
                  .value();

                return dispatch(setPrices(newPrices));
              };

              const price = prices?.[key];
              const { min, max } = price || {};

              return (
                <Fragment
                  key={key}
                >
                  <div className="flex flex-col md:flex-row">
                    <div>
                      {
                        fieldConfigs.length > 1 && (
                          // Only show subheadings, if there are more than 1 prices to fill
                          <div className='mt-6'>
                            <Heading
                              level="h3"
                              margin={0}
                              text={t([
                                `topics:price:heading:${key}`,
                                `topics:price:heading:general`,
                              ])}
                              optionalField={true}
                            />
                          </div>
                        )
                      }
                      <div className="flex h-16 space-x-2 xs:mb-5 md:mb-0 font-bold text-6xl">
                        <div className="text-2xl xs:w-32 md:w-40">
                          <Input
                            initialValue={min}
                            type="number"
                            placeholder="0.00"
                            onChange={onChange.bind(null, 'min')}
                          />
                        </div>
                        <div className="mx-2">-</div>
                        <div className="text-2xl xs:w-32 md:w-40">
                          <Input
                            initialValue={max}
                            type="number"
                            placeholder="0.00"
                            onChange={onChange.bind(null, 'max')}
                          />
                        </div>
                        {fieldConfigs.length > 1 && <div className='ml-2'>{UnitDisplay}</div>}
                      </div>
                      <div className="mt-2">
                        <InfoText
                          text={t('labels:price', {
                            currency: countryMarketConfig?.currency,
                            weightUnit: countryMarketConfig?.weightUnit,
                          })}
                          color="dark"
                          variant="small"
                        />
                      </div>
                    </div>
                    {fieldConfigs.length === 1 && UnitDisplay}
                  </div>
                </Fragment>
              );
            })}
          </div>
        </TemplateFlexGridContainer.BriefingStepSection>
      </TemplateFlexGridContainer.BriefingStepBody>
    </TemplateFlexGridContainer.BriefingStep>
  );
};
